import React, {useEffect, useContext, useMemo} from 'react';
import { Box, Button, Stack, TableCell, TextField, MenuItem, Typography, IconButton, Dialog, Backdrop, CircularProgress, Snackbar } from '@mui/material';
import {Context} from '../App';
import SorTableCheckable from '../components/ui/sortable-checkable';
import { Add, AttachEmail, DeleteForever, Edit, InfoOutlined } from '@mui/icons-material';
import moment from 'moment';
import AppLayout from '../components/layout/AppLayout';
import BilletsForm from '../components/forms/billets';
import SendBillets from '../components/forms/sendBillets';
import Billet from '../components/ui/Billet';
import BilletEditForm from '../components/forms/billetEdit';

export default function Billets() {
    const {setIsConnected} = useContext(Context)
    const user = JSON.parse(sessionStorage.getItem('user'))
    const [load, setLoad] = React.useState(true);
    const [open, setOpen] = React.useState(false);
    const [action, setAction] = React.useState('');
    const [events, setEvents] = React.useState([]);
    const [eventId, setEventId] = React.useState('');
    const [billets, setBillets] = React.useState([]);
    const [billet, setBillet] = React.useState();
    const [selectItems, setItems] = React.useState([]);
    const [searchInput, setSearchInput] = React.useState('');
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackMsg, setSnackMsg] = React.useState('');

    const event = useMemo(()=>events.find(p=>p._id===eventId), [eventId, events]);

    const table = [
        {header: "N°", sortable: true, sortBy:(el)=>el.num, row: (row, index)=><TableCell key={index} style={{minWidth:'160px'}}>{row.num}</TableCell>},
        {header: "Date", sortable: true, sortBy:(el)=>new Date(el.createdAt).getTime(), row: (row, index)=><TableCell key={index} sx={{minWidth:'70px', textAlign:'center'}}>{moment(row.createdAt).format('DD/MM/yyyy')}</TableCell>},
        {header: "Participant", sortable: true, sortBy:(el)=>(el.owner?.nom+' '+el.owner?.prenom).toLowerCase(), row: (row, index)=><TableCell key={index} style={{textTransform:'capitalize', minWidth:'200px'}}>{row.owner? (row.owner.nom+' '+row.owner.prenom): ''}</TableCell>},
        {header: "Status", sortable: true, sortBy:(el)=>el.statut.toLowerCase(), row: (row, index)=><TableCell key={index} sx={{minWidth:'50px', textAlign:'center'}}>{row.statut}</TableCell>},
        {header: "Tarif", sortable: true, sortBy:(el)=>el.price, row: (row, index)=><TableCell key={index} style={{minWidth:'200px'}}>{row.tarif+' - '+row.price?.toFixed(2)} €</TableCell>},
        {header: "", sortable: false, row: (row, index)=><TableCell key={index} sx={{minWidth:'180px', textAlign:'end'}} >
            <IconButton aria-label="visualiser le billet" onClick={()=>{setBillet(row); manage_dialog(true, 'ticket')}}><InfoOutlined fontSize="small"/></IconButton>
            <IconButton aria-label="modifier" onClick={()=>{setBillet(row); manage_dialog(true, 'edit')}}><Edit fontSize="small"/></IconButton>
            <IconButton aria-label="envoyer par mail" disabled={row.statut !== 'valide'} onClick={()=>{setBillet(row); manage_dialog(true, 'send')}}><AttachEmail fontSize="small"/></IconButton>
        </TableCell>}
    ]

    function normalize(str) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s-.€]/gi,'').toLowerCase()
    }

    function get_searchable(item) {
        return item.owner.nom+' '+item.owner.prenom+' '+item.num
    }

    function searchFilter(a, b) {
        return normalize(get_searchable(a)).includes(normalize(b))
    }

    function manage_dialog(status, action='') {
        setOpen(status)
        setAction(action)
        if(!status) {
            setBillet()
        }
    }

    function manage_snackbar(status, msg='') {
        setOpenSnackbar(status)
        setSnackMsg(msg)
        if(!status) {
            setSnackMsg('')
        }
    }

    useEffect(()=> {
        if (!(!!eventId)) return;
        setLoad(true);

        const query = new URLSearchParams({event: eventId, statut: 'valide'}).toString();

        fetch(process.env.REACT_APP_API_URL.concat('/billets?'+query), {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.status === 200) {
                    const json = await response.json();
                    setBillets(json);
                }
            }
        }).catch(error => {
            console.error("Error fetching billets:", error);
        }).finally(() => {
            setLoad(false);
        });
    }, [eventId])

    useEffect(()=> {
        if(events.length > 0 && !eventId) {
            if (events.length === 1) {
                setEventId(events[0]._id);
            } else {
                const nearestEvent = events.reduce((prev, curr) => {
                    const prevDate = new Date(prev.date);
                    const currDate = new Date(curr.date);
                    return (prevDate < currDate) ? prev : curr;
                });
                setEventId(nearestEvent._id);
            }
        }
    },  [events])

    useEffect(() => {
        if (!user || user.role === 'user') {
            sessionStorage.clear();
            setIsConnected(false);
        }

        if (user.role === 'admin' && user.events.length === 1) {
            setEventId(user.events[0]);
            return;
        }

        const query = user.role==='admin' ? {_id: {$in: user.events}} :{};

        fetch(process.env.REACT_APP_API_URL.concat('/events'), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'authorization': 'Bearer ' + sessionStorage.getItem('token')
            }, body: JSON.stringify(query)
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.status === 200) {
                    const json = await response.json();
                    setEvents(json);
                }
            }
        }).catch(error => {
            console.error("Error fetching events:", error);
        }).finally(() => {
            setLoad(false);
        });
    }, []);

    return(
        <AppLayout>
            <Box flex={1} display='flex' flexDirection='column' sx={{padding:2}}>

                <Typography variant='h2' marginBlock={3} align='center'>Gestionnaire billetterie</Typography>

                <Button variant="outlined" color="focus" onClick={()=>manage_dialog(true, 'add')} startIcon={<Add/>} sx={{marginBottom:2, alignSelf:'end', borderColor:'focus.main'}}>Nouveau billet</Button>

                <Stack direction="row" spacing={2} marginBottom={1.5} alignItems='center' >
                    {(user?.role==='super admin' || user.events.length > 1) && <TextField select label='Évènement' value={eventId} onChange={(e) => setEventId(e.target.value)} size="small" sx={{minWidth:'200px', width:'30%'}}>
                        {events.map((p, index) => (
                            <MenuItem key={index} value={p._id}>{p.titre}</MenuItem>
                        ))}
                    </TextField>}
                    <TextField value={searchInput} onChange={(e)=>setSearchInput(e.target.value)} label="Rechercher un billet..." variant="outlined" size="small" sx={{width:'60%'}}/>
                    {/* <Button variant="contained" color="primary" onClick={()=>manage_dialog(true, 'add')} startIcon={<Add/>}>
                        Nouveau
                    </Button> */}
                </Stack>

                <SorTableCheckable {...{data:billets.filter(reg=>searchFilter(reg, searchInput)),table, selectItems, setItems, rowsPerPage:25, rowsPerPageOptions:[25,50,100]}}/>
            </Box>

            <Dialog {...{open, onClose:()=>manage_dialog(false), maxWidth:action==='send'?'sm':'md', fullWidth:true}} >
                {action === 'ticket' && <Billet billet={billet} />}
                {action === 'add' && <BilletsForm {...{events, selectedEvent: eventId, close:()=>manage_dialog(false), updateBillets: bls => {if(bls[0].events=== eventId) setBillets(old=> [...old, ...bls])}}}/>}
                {action === 'edit' && <BilletEditForm {...{event, billet, updateBillet: blt => setBillets(old => old.map(b => b._id === blt._id ? blt : b)), close:()=>manage_dialog(false)}} />}
                {action === 'send' && <SendBillets {...{billets: billet ? [billet._id] : selectItems, close:()=>manage_dialog(false), setSnack: (msg) => manage_snackbar(true, msg)}}/>}
            </Dialog>

            <Snackbar open={openSnackbar} autoHideDuration={5000} onClose={()=> manage_snackbar(false)} message={snackMsg} />

            <Backdrop open={load}><CircularProgress/></Backdrop>
        </AppLayout>
    )
}

